/**
 * Persistence of the state of a list.
 *
 * What DataTables owns - current page, page length, sort, content of the search box - is
 * saved by DataTables itself. What it knows nothing about is the filters of the advanced
 * search modal, which live in the component: they travel in the same payload, and are
 * pushed back to the search form with the '{id}-filter-restore' event on restore.
 *
 * The storage key is built on the id of the table rather than on the DataTables default
 * (table id + location.pathname), so a list keeps a single entry whatever the URL it is
 * reached from, instead of one entry per record on the pages which carry an id in their
 * path. Nothing expires, so that count has to stay bounded: there is one key per table of
 * the application, and there is no other way to grow that number.
 *
 * Two tables may share an id - the notification widget and the "other notifications" list
 * do, because the notification modal listens to that namespace - and tell their states
 * apart with `key`. A table whose content depends on a record - the candidates of a group
 * - declares that record as its `scope`: the scope travels in the payload and the state is
 * dropped when it changes, so the filters of one group never apply to the next one.
 */

const KEY_PREFIX = 'polaris.table.';

/**
 * Is persistence enabled on this table?
 *
 * The value may be a boolean or one of the "0" / "1" strings the table components accept
 * on their other props.
 *
 * @param  {*}  value
 * @return {boolean}
 */
function enabled(value) {
    if (typeof value === 'string') {
        return value !== '' && value !== '0';
    }
    return !!value;
}

/**
 * Build the DataTables options which persist the state of a table.
 *
 * The component provides `id`, the bus to notify on `bus`, and the filters of its advanced
 * search on `filters`. A table with no advanced search declares no `filters` and simply
 * persists what DataTables owns.
 *
 * The returned object is meant to be merged into the DataTables init.
 *
 * @param  {Vue}  component
 * @param  {{save: *, key: ?string, scope: ?*}}  options
 * @return {object}
 */
export default function tableState(component, options) {

    options = options ? options : {};

    var key = KEY_PREFIX + (options.key ? options.key : component.id);

    var scope = options.scope === undefined || options.scope === null
        ? null
        : String(options.scope);

    var forget = function () {
        try {
            localStorage.removeItem(key);
        } catch (e) {
            // Nothing to forget when there is no storage.
        }
    };

    return {

        stateSave: enabled(options.save),

        // No expiration: the point is to find a list as it was left, whenever that was.
        // DataTables still drops a state whose number of columns changed, so adding a
        // column to a list is not a trap.
        stateDuration: 0,

        stateSaveCallback: function (settings, data) {
            try {
                localStorage.setItem(key, JSON.stringify(data));
            } catch (e) {
                // Storage full or unavailable: the table works, it just does not remember.
            }
        },

        stateLoadCallback: function (settings) {
            try {
                return JSON.parse(localStorage.getItem(key));
            } catch (e) {
                return null;
            }
        },

        stateSaveParams: function (settings, data) {
            if (component.filters !== undefined) {
                data.filters = component.filters;
            }
            data.scope = scope;
        },

        stateLoadParams: function (settings, data) {

            // Returning false is how DataTables is told to ignore the state.
            if (scope !== null && data.scope !== scope) {
                forget();
                return false;
            }

            if (component.filters !== undefined) {
                component.filters = data.filters ? data.filters : {};
                if (component.bus) {
                    component.bus.$emit(component.id + '-filter-restore', component.filters);
                }
            }
        }
    };
}
